import { ComponentType } from 'react';
import { Entity } from '@backstage/catalog-model';
import {
  compatWrapper,
  convertLegacyPlugin,
  convertLegacyRouteRef,
} from '@backstage/core-compat-api';
import { PageBlueprint } from '@backstage/frontend-plugin-api';
import { EntityCardBlueprint } from '@backstage/plugin-catalog-react/alpha';
import {
  EntityFluxDeploymentsCard,
  EntityFluxGitRepositoriesCard,
  EntityFluxHelmReleasesCard,
  EntityFluxHelmRepositoriesCard,
  EntityFluxImagePoliciesCard,
  EntityFluxKustomizationsCard,
  EntityFluxOCIRepositoriesCard,
  EntityFluxSourcesCard,
  FluxIcon,
  FluxRuntimeCard,
  FluxRuntimePage,
  fluxPlugin,
} from '@backstage-community/plugin-flux';

// Flux cards only make sense for entities mapped to Kubernetes resources, i.e.
// those carrying the kubernetes-label-selector annotation that the kubernetes
// plugin uses to look resources up.
const hasKubernetesLabelSelector = (entity: Entity) =>
  Boolean(
    entity.metadata.annotations?.['backstage.io/kubernetes-label-selector'],
  );

const makeEntityCard = (name: string, Card: ComponentType<{ many?: boolean }>) =>
  EntityCardBlueprint.make({
    name,
    params: {
      filter: hasKubernetesLabelSelector,
      // Flux components are legacy-frontend; wrap so legacy APIs resolve.
      loader: async () => compatWrapper(<Card />),
    },
  });

const fluxRuntimePage = PageBlueprint.make({
  params: {
    path: '/flux',
    title: 'Flux',
    icon: <FluxIcon />,
    routeRef: convertLegacyRouteRef(fluxPlugin.routes.root),
    loader: async () => compatWrapper(<FluxRuntimePage />),
  },
});

const helmReleasesCard = makeEntityCard(
  'helm-releases',
  EntityFluxHelmReleasesCard,
);
const gitRepositoriesCard = makeEntityCard(
  'git-repositories',
  EntityFluxGitRepositoriesCard,
);
const ociRepositoriesCard = makeEntityCard(
  'oci-repositories',
  EntityFluxOCIRepositoriesCard,
);
const helmRepositoriesCard = makeEntityCard(
  'helm-repositories',
  EntityFluxHelmRepositoriesCard,
);
const kustomizationsCard = makeEntityCard(
  'kustomizations',
  EntityFluxKustomizationsCard,
);
const deploymentsCard = makeEntityCard(
  'deployments',
  EntityFluxDeploymentsCard,
);
const sourcesCard = makeEntityCard('sources', EntityFluxSourcesCard);
const imagePoliciesCard = makeEntityCard(
  'image-policies',
  EntityFluxImagePoliciesCard,
);
const runtimeCard = makeEntityCard('runtime', FluxRuntimeCard);

// Flux is a legacy-frontend plugin (no /alpha); convertLegacyPlugin registers
// its apis and routes in the new frontend system. The /flux runtime page and
// the entity cards are added on top.
export const fluxModule = convertLegacyPlugin(fluxPlugin, {
  extensions: [
    fluxRuntimePage,
    helmReleasesCard,
    gitRepositoriesCard,
    ociRepositoriesCard,
    helmRepositoriesCard,
    kustomizationsCard,
    deploymentsCard,
    sourcesCard,
    imagePoliciesCard,
    runtimeCard,
  ],
});